import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Stack,
  Text,
} from "@aksara-ui/react";
import React, { useRef, useState } from "react";
import styled, { keyframes, css } from "styled-components";
import ApiService from "service/Api";
import CommonMedal from "assets/images/background_card.png";

/* ================= ANIMATION ================= */
const roll = keyframes`
  0% { transform: scale(1); filter: blur(0); }
  50% { transform: scale(1.05); filter: blur(1px); }
  100% { transform: scale(1); filter: blur(0); }
`;

const confettiFall = keyframes`
  0% { transform: translateY(-100vh) rotate(0deg); opacity: 1; }
  100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
`;

const WinnerName = styled(Text)<{ animate?: boolean }>`
  ${({ animate }) =>
    animate &&
    css`
      animation: ${winnerPop} 0.6s ease-out;
    `}
`;

const winnerPop = keyframes`
  0% { transform: scale(0.8); opacity: 0; }
  60% { transform: scale(1.15); opacity: 1; }
  100% { transform: scale(1); }
`;

/* ================= CONFETTI ================= */
const ConfettiLayer = styled(Box)`
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 9999;
`;

const ConfettiPiece = styled(Box)<{ delay: number; left: number }>`
  position: absolute;
  top: 0;
  left: ${({ left }) => left}%;
  width: 10px;
  height: 16px;
  border-radius: 3px;
  background: hsl(${() => Math.random() * 360}, 90%, 60%);
  animation: ${confettiFall} 3s linear infinite;
  animation-delay: ${({ delay }) => delay}s;
`;

/* ================= LAYOUT ================= */
const Wrapper = styled(Box)`
  height: 100%;
  min-height: 0;
  display: grid;
  grid-template-columns: 2.2fr 1fr;

  background-image:
    url(${CommonMedal}),
    linear-gradient(#1645a8, #1645a8);
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
`;

/* ================= LEFT ================= */
const Stage = styled(Box)<{ rolling?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;
  color: white;
  padding: 48px;

  ${({ rolling }) =>
    rolling &&
    css`
      animation: ${roll} 0.25s infinite;
    `}
`;

const PrizeCard = styled(Box)`
  width: 80%;
  padding: 36px;
  border-radius: 24px;
  border: 2px solid #0210d4;
  background: #ffffff;
  box-shadow: 0 0 60px rgba(12, 194, 185, 0.35);
  text-align: center;
`;

const NumberCard = styled(Box)`
  width: 80%;
  padding: 40px;
  border-radius: 24px;
  background: #ffffff;
  text-align: center;
`;

const DigitBox = styled(Box)`
  width: 64px;
  height: 84px;
  border-radius: 14px;
  border: 2px solid #1547fa;
  font-size: 36px;
  font-weight: 900;
  color: #070707;
  background: #ecedee;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DrawButton = styled(Button)`
  height: 72px;
  min-width: 320px;
  font-size: 22px;
  font-weight: 900;
`;

/* ================= RIGHT ================= */
const SidePanel = styled(Box)`
  padding: 36px;
  background: linear-gradient(180deg, #e8f5f9, #e8f5f9);
  border-left: 2px solid rgba(250, 204, 21, 0.25);

  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
`;

const WinnerList = styled(Box)`
  margin-top: 16px;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
`;

const WinnerItem = styled(Box)`
  padding: 16px;
  border-radius: 14px;
  background: #1f60ee;
  color: #e5e7eb;
  font-weight: 800;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

/* ================= PROPS ================= */
interface Props {
  catalogId: string;
  vouchers: any[];
  isOpen: boolean;
  onClose: () => void;
}

/* ================= COMPONENT ================= */
const RaffleLuxuryModal: React.FC<Props> = ({
  catalogId,
  vouchers,
  isOpen,
  onClose,
}) => {
  const [digits, setDigits] = useState<string[]>(Array(9).fill("0"));
  const [rolling, setRolling] = useState(false);
  const [loadingApi, setLoadingApi] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [winners, setWinners] = useState<
    { code: string; name: string }[]
  >([]);

  const MAX_DRAW = 10;
  const isLimitReached = winners.length >= MAX_DRAW;
  const timer = useRef<NodeJS.Timeout | null>(null);

  const voucherDetail = vouchers.find(
    (v) => v.id === parseInt(catalogId)
  );
  const voucherName = voucherDetail?.catalog_name || "GRAND RAFFLE";

  const randomRollingDigits = () =>
    Array.from({ length: 9 }, () =>
      Math.floor(Math.random() * 10).toString()
    );

  const draw = async () => {
  if (rolling || loadingApi) return;

  if (winners.length >= MAX_DRAW) {
    alert("Undian sudah mencapai batas maksimal 10 kali");
    return;
  }

  setRolling(true);
  setLoadingApi(true);

  timer.current = setInterval(() => {
    setDigits(randomRollingDigits());
  }, 60);

  try {
    const res = await ApiService.apiRequest({
      method: "post",
      url: "/admin/raffle-ancol/draw",
      data: { catalog_id: catalogId },
    });

    const data = res.data;

    setTimeout(() => {
      if (timer.current) clearInterval(timer.current);

      setDigits(
        data.coupon_code.replace("GBYR-", "").split("")
      );

      setWinners((prev) => [
        { code: data.coupon_code, name: data.username },
        ...prev,
      ]);

      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 3000);

      setRolling(false);
      setLoadingApi(false);
    }, 2500);
  } catch (err) {
    if (timer.current) clearInterval(timer.current);
    setRolling(false);
    setLoadingApi(false);
    alert("Undian gagal");
  }
};

  const handleClose = () => {
    if (timer.current) clearInterval(timer.current);
    setDigits(Array(9).fill("0"));
    setWinners([]);
    setShowConfetti(false);
    setRolling(false);
    setLoadingApi(false);
    onClose();
  };

  return (
    <>
      <Dialog isOpen={isOpen} onClose={handleClose} maxWidth="200vw">
        <DialogContent
          style={{
            padding: 0,
            height: "100%",
            overflow: "hidden",
          }}
        >
          <Wrapper>
            {/* LEFT */}
            <Stage rolling={rolling}>
              <PrizeCard>
                <Text fontSize="28px" fontWeight={900} color="black">
                  Hadiah Bulanan
                  <br />
                  <br />
                  {voucherName}
                </Text>
              </PrizeCard>

              <NumberCard>
                <Box display="flex" justifyContent="center" alignItems="center" mb="lg">
                  <Text fontSize="30px" fontWeight={900} color="black">
                    GBYR-
                  </Text>
                  <Box display="flex">
                    {digits.map((d, i) => (
                      <DigitBox key={i}>{d}</DigitBox>
                    ))}
                  </Box>
                </Box>

                <Text color="grey06" fontSize="20px">
                  Selamat kepada
                </Text>
                <br />
                <br />
                <WinnerName
                  fontSize="26px"
                  fontWeight={900}
                  color="black"
                  animate={winners.length > 0}
                >
                  {winners[0]?.name || "-"}
                </WinnerName>

              </NumberCard>

              <DrawButton
                variant="primary"
                onClick={draw}
                disabled={rolling || loadingApi || isLimitReached}
                style={{
                  color: isLimitReached ? "#000" : undefined,
                }}
              >
                {isLimitReached
                  ? "BATAS UNDIAN TERCAPAI"
                  : rolling
                  ? "MENGUNDI..."
                  : "UNDI SEKARANG"}
              </DrawButton>
              <Text
                fontSize="16px"
                fontWeight={700}
                color={isLimitReached ? "red" : "black"}
              >
                Status Undian: {winners.length} / {MAX_DRAW}
                {isLimitReached && " (Batas tercapai)"}
              </Text>
            </Stage>

            {/* RIGHT */}
            <SidePanel>
              <Text fontSize="24px" fontWeight={900} color="primary">
                Daftar Pemenang
              </Text>

              <WinnerList>
                <Stack spacing="sm">
                  {winners.map((w) => (
                    <WinnerItem key={w.code}>
                      <Box>
                        <Text fontSize={18} fontWeight={900}>
                          {w.code}
                        </Text>
                        <br />
                        <Text fontSize="14px">{w.name}</Text>
                      </Box>
                      🏆
                    </WinnerItem>
                  ))}
                </Stack>
              </WinnerList>
            </SidePanel>
          </Wrapper>
        </DialogContent>
      </Dialog>

      {showConfetti && (
        <ConfettiLayer>
          {Array.from({ length: 50 }).map((_, i) => (
            <ConfettiPiece
              key={i}
              delay={Math.random() * 2}
              left={Math.random() * 100}
            />
          ))}
        </ConfettiLayer>
      )}
    </>
  );
};

export default RaffleLuxuryModal;
